import React, { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Divider,
  Flex,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconCheck, IconDeviceFloppy, IconEdit, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { OpioidConversionVersionApi } from '../../../global/apis/OpioidConversionVersionApi/OpioidConversionVersionApi';
import { OpioidConversionFactorApi } from '../../../global/apis/OpioidConversionFactorApi/OpioidConversionFactorApi';
import { MMEApi } from '../../../global/apis/MMEApi/MMEApi';
import { IOpioidConversionVersion } from '../../../global/entities/MME/MME';
import { IOpioidConversionFactor } from '../../../global/entities/OpioidConversionFactor/OpioidConversionFactor';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { theme } from '../../../global/style/theme.tsx';

const OpioidConversionVersionForm: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUsedInCalculations, setIsUsedInCalculations] = useState(false);
  
  const [version, setVersion] = useState<IOpioidConversionVersion | null>(null);
  const [conversionFactors, setConversionFactors] = useState<IOpioidConversionFactor[]>([]);
  const [fentanylConfig, setFentanylConfig] = useState<any>(null);
  const [methadoneConfig, setMethadoneConfig] = useState<any>(null);
  const [buprenorphineConfig, setBuprenorphineConfig] = useState<any>(null);
  const [riskThresholds, setRiskThresholds] = useState<any>(null);

  // Dropdown options
  const [drugs, setDrugs] = useState<{ value: string; label: string }[]>([]);
  const [routes, setRoutes] = useState<{ value: string; label: string }[]>([]);

  // Edit mode states
  const [editedVersionName, setEditedVersionName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedConversionFactors, setEditedConversionFactors] = useState<IOpioidConversionFactor[]>([]);
  const [editedFentanylConfig, setEditedFentanylConfig] = useState<any>(null);
  const [editedMethadoneConfig, setEditedMethadoneConfig] = useState<any>(null);
  const [editedBuprenorphineConfig, setEditedBuprenorphineConfig] = useState<any>(null);
  const [editedRiskThresholds, setEditedRiskThresholds] = useState<any>(null);

  useEffect(() => {
    loadVersionData();
    loadDrugRoutesAndUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDrugRoutesAndUnits = async () => {
    try {
      const combinations = await MMEApi.getAllDrugRouteCombinationsForAdmin();

      // Extract unique drugs
      const drugMap = new Map();
      combinations.forEach((c) => {
        if (!drugMap.has(c.drugID)) {
          drugMap.set(c.drugID, {
            value: c.drugID.toString(),
            label: c.drugNameEn,
          });
        }
      });
      setDrugs(Array.from(drugMap.values()));

      // Extract unique routes
      const routeMap = new Map();
      combinations.forEach((c) => {
        if (!routeMap.has(c.routeID)) {
          routeMap.set(c.routeID, {
            value: c.routeID.toString(),
            label: c.routeNameEn,
          });
        }
      });
      setRoutes(Array.from(routeMap.values()));
    } catch (error) {
      console.error('Error loading drugs, routes, and units:', error);
    }
  };

  const loadVersionData = async () => {
    try {
      setIsLoading(true);
      const id = parseInt(params.id!);
      
      // Load version details
      const versionData = await OpioidConversionVersionApi.getById(id);
      setVersion(versionData);
      setRiskThresholds(versionData.riskThresholds || null);

      // Check if version is used in calculations
      const canDeleteInfo = await OpioidConversionVersionApi.canDeleteVersion(id);
      setIsUsedInCalculations(canDeleteInfo.isUsedInCalculations);

      // Load conversion factors for this specific version
      const factors = await OpioidConversionFactorApi.getAll(id);
      setConversionFactors(factors);

      // Load special formula configurations
      try {
        const fentanyl = await OpioidConversionVersionApi.getFormulaConfig(id, 'TransdermalFentanyl');
        setFentanylConfig(fentanyl);
      } catch {
        console.log('No fentanyl config found');
      }

      try {
        const methadone = await OpioidConversionVersionApi.getFormulaConfig(id, 'Methadone');
        setMethadoneConfig(methadone);
      } catch {
        console.log('No methadone config found');
      }

      try {
        const buprenorphine = await OpioidConversionVersionApi.getFormulaConfig(id, 'Buprenorphine');
        setBuprenorphineConfig(buprenorphine);
      } catch {
        console.log('No buprenorphine config found');
      }

    } catch (error) {
      console.error('Error loading version data:', error);
      notifications.show({
        title: t('opioidConversionVersionForm.saveErrorTitle'),
        message: t('opioidConversionVersionForm.saveErrorMessage'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMode = () => {
    // Copy current data to edit states
    setEditedVersionName(version?.versionName || '');
    setEditedDescription(version?.description || '');
    setEditedConversionFactors(JSON.parse(JSON.stringify(conversionFactors)));
    setEditedFentanylConfig(fentanylConfig ? JSON.parse(JSON.stringify(fentanylConfig)) : null);
    setEditedMethadoneConfig(methadoneConfig ? JSON.parse(JSON.stringify(methadoneConfig)) : null);
    setEditedBuprenorphineConfig(buprenorphineConfig ? JSON.parse(JSON.stringify(buprenorphineConfig)) : null);
    setEditedRiskThresholds(riskThresholds ? JSON.parse(JSON.stringify(riskThresholds)) : { moderateThreshold: 50, highThreshold: 90, isBlocking: false });
    setIsEditMode(true);
  };

  const hasUnsavedChanges = () => {
    // Check if version name or description changed
    if (editedVersionName !== (version?.versionName || '')) return true;
    if (editedDescription !== (version?.description || '')) return true;

    // Check if conversion factors changed
    if (JSON.stringify(editedConversionFactors) !== JSON.stringify(conversionFactors)) return true;

    // Check if special formula configs changed
    if (JSON.stringify(editedFentanylConfig) !== JSON.stringify(fentanylConfig)) return true;
    if (JSON.stringify(editedMethadoneConfig) !== JSON.stringify(methadoneConfig)) return true;
    if (JSON.stringify(editedBuprenorphineConfig) !== JSON.stringify(buprenorphineConfig)) return true;

    // Check if risk thresholds changed
    if (JSON.stringify(editedRiskThresholds) !== JSON.stringify(riskThresholds)) return true;

    return false;
  };

  const handleCancelEdit = () => {
    // If no changes were made, just exit edit mode
    if (!hasUnsavedChanges()) {
      setIsEditMode(false);
      return;
    }

    // Show confirmation modal only if there are unsaved changes
    modals.openConfirmModal({
      title: t('opioidConversionVersionForm.cancelEditingTitle'),
      children: (
        <Text size="sm">
          {t('opioidConversionVersionForm.cancelEditingMessage')}
        </Text>
      ),
      labels: { confirm: t('opioidConversionVersionForm.yesCancelLabel'), cancel: t('opioidConversionVersionForm.continueEditingLabel') },
      confirmProps: { color: 'red' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: () => {
        setIsEditMode(false);
      },
      size: 'md',
      radius: 'xs',
    });
  };

  const handleSaveChanges = async () => {
    const isUpdate = !isUsedInCalculations;
    
    modals.openConfirmModal({
      title: isUpdate ? t('opioidConversionVersionForm.saveChangesTitle') : t('opioidConversionVersionForm.saveAsNewVersionTitle'),
      children: (
        <Stack gap={'sm'}>
          {isUpdate ? (
            <>
              <Text size="sm">
                {t('opioidConversionVersionForm.saveChangesMessage')}
              </Text>
              <Text size="sm" c="dimmed">
                {t('opioidConversionVersionForm.saveChangesNote')}
              </Text>
            </>
          ) : (
            <>
              <Text size="sm">
                {t('opioidConversionVersionForm.saveAsNewVersionMessage')}
              </Text>
              <Text size="sm" c="dimmed">
                {t('opioidConversionVersionForm.saveAsNewVersionNote')}
              </Text>
            </>
          )}
        </Stack>
      ),
      labels: { confirm: isUpdate ? t('opioidConversionVersionForm.saveLabel') : t('opioidConversionVersionForm.saveAsNewVersionLabel'), cancel: t('opioidConversionVersionForm.cancelLabel') },
      confirmProps: { color: 'blue' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: async () => {
        try {
          setIsSaving(true);

          // Prepare the data for the API
          const dataModel = {
            versionName: editedVersionName,
            description: editedDescription,
            activateImmediately: false,
            conversionFactors: editedConversionFactors.map(f => ({
              drugID: f.drugID,
              drugName: f.drugName,
              routeID: f.routeID,
              routeName: f.routeName,
              conversionFactor: f.conversionFactor,
            })),
            specialFormulaConfigs: {
              transdermalFentanyl: editedFentanylConfig,
              methadone: editedMethadoneConfig,
              buprenorphine: editedBuprenorphineConfig,
            },
            riskThresholds: editedRiskThresholds,
          };

          if (isUpdate) {
            // Update existing version
            await OpioidConversionVersionApi.updateVersion(version!.id, dataModel);
            
            notifications.show({
              title: t('opioidConversionVersionForm.saveSuccessTitle'),
              message: t('opioidConversionVersionForm.saveSuccessMessage', { versionName: editedVersionName }),
              color: 'green',
            });
            
            setIsEditMode(false);
            loadVersionData();
          } else {
            // Create new version
            const response = await OpioidConversionVersionApi.createVersion(dataModel);
            
            notifications.show({
              title: t('opioidConversionVersionForm.saveSuccessTitle'),
              message: t('opioidConversionVersionForm.createSuccessMessage', { versionName: editedVersionName }),
              color: 'green',
            });
            
            setIsEditMode(false);
            
            // Navigate to the new version
            if (response.data && response.data.id)
              navigate(`/adminDashboard/conversion-versions/${response.data.id}`);
            else
              loadVersionData();
          }
        } catch (error) {
          console.error('Error saving changes:', error);
          notifications.show({
            title: t('opioidConversionVersionForm.saveErrorTitle'),
            message: t('opioidConversionVersionForm.saveErrorMessage'),
            color: 'red',
          });
        } finally {
          setIsSaving(false);
        }
      },
      size: 'lg',
      radius: 'xs',
    });
  };

  const handleActivateVersion = () => {
    if (!version) return;

    modals.openConfirmModal({
      title: t('opioidConversionVersionForm.activateVersionTitle'),
      children: (
        <Stack gap={'sm'}>
          <Text size="sm">
            {t('opioidConversionVersionForm.activateVersionMessage', { versionName: version.versionName })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('opioidConversionVersionForm.activateVersionNote')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('opioidConversionVersionForm.activateLabel'), cancel: t('opioidConversionVersionForm.cancelLabel') },
      confirmProps: { color: 'blue' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: async () => {
        try {
          await OpioidConversionVersionApi.activateVersion(version.id);
          notifications.show({
            title: t('opioidConversionVersionForm.activateSuccessTitle'),
            message: t('opioidConversionVersionForm.activateSuccessMessage', { versionName: version.versionName }),
            color: 'green',
          });
          loadVersionData();
        } catch (error) {
          console.error('Error activating version:', error);
          notifications.show({
            title: t('opioidConversionVersionForm.activateErrorTitle'),
            message: t('opioidConversionVersionForm.activateErrorMessage'),
            color: 'red',
          });
        }
      },
      size: 'lg',
      radius: 'xs',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading)
    return <Loading visible={true} loadingMessage={t('opioidConversionVersionForm.loadingVersionData')} />;

  if (!version) {
    return (
      <Paper p="xl">
        <Text>{t('opioidConversionVersionForm.versionNotFound')}</Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md" mt="md">
      {/* Header */}
      <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
        <Flex align={'center'} gap={'md'}>
          <Button
            leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
            onClick={() => navigate('/adminDashboard/conversion-versions')}
            variant="transparent"
            disabled={isEditMode}
          >
            <Title c="black" order={4}>
              {version.versionName}
            </Title>
          </Button>
          {version.isActive && (
            <Badge color="green" size="lg">{t('opioidConversionVersionForm.active')}</Badge>
          )}
          {!version.isActive && (
            <Badge color="gray" size="lg">{t('opioidConversionVersionForm.inactive')}</Badge>
          )}
          {isEditMode && (
            <Badge color="blue" size="lg">{t('opioidConversionVersionForm.editing')}</Badge>
          )}
        </Flex>
        <Group>
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                color="gray"
                leftSection={<IconX size={16} />}
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                {t('opioidConversionVersionForm.cancel')}
              </Button>
              <Button
                color="blue"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={handleSaveChanges}
                loading={isSaving}
              >
                {isUsedInCalculations ? t('opioidConversionVersionForm.saveAsNewVersion') : t('opioidConversionVersionForm.save')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                leftSection={<IconEdit size={16} />}
                onClick={handleEditMode}
              >
                {t('opioidConversionVersionForm.edit')}
              </Button>
              {!version.isActive && (
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={handleActivateVersion}
                >
                  {t('opioidConversionVersionForm.activate')}
                </Button>
              )}
            </>
          )}
        </Group>
      </Flex>

      {/* Version Details */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          {isEditMode && (
            <div>
              <Text size="sm" fw={500} c="dimmed" mb={4}>{t('opioidConversionVersionForm.versionName')}</Text>
              <TextInput
                value={editedVersionName}
                onChange={(e) => setEditedVersionName(e.currentTarget.value)}
                placeholder={t('opioidConversionVersionForm.enterVersionName')}
                required
              />
            </div>
          )}
          <div>
            <Text size="sm" fw={500} c="dimmed" mb={4}>{t('opioidConversionVersionForm.description')}</Text>
            {isEditMode ? (
              <TextInput
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.currentTarget.value)}
                placeholder={t('opioidConversionVersionForm.enterDescription')}
              />
            ) : (
              <Text size="sm">{version.description || t('opioidConversionVersionForm.noDescription')}</Text>
            )}
          </div>
          <div>
            <Text size="sm" fw={500} c="dimmed">{t('opioidConversionVersionForm.createdDate')}</Text>
            <Text size="sm" mt={4}>{formatDate(version.createdDate)}</Text>
          </div>
        </Stack>
      </Paper>

      {/* Conversion Data Accordion */}
      <Accordion defaultValue="conversion-factors" variant="separated">
        {/* Conversion Factors */}
        <Accordion.Item value="conversion-factors">
          <Accordion.Control>
            <Group>
              <Text fw={500}>{t('opioidConversionVersionForm.standardConversionFactors')}</Text>
              <Badge>{isEditMode ? editedConversionFactors.length : conversionFactors.length}</Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('opioidConversionVersionForm.drug')}</Table.Th>
                  <Table.Th>{t('opioidConversionVersionForm.route')}</Table.Th>
                  <Table.Th>{t('opioidConversionVersionForm.conversionFactor')}</Table.Th>
                  {isEditMode && <Table.Th style={{ width: 80 }}>{t('opioidConversionVersionForm.actions')}</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(isEditMode ? editedConversionFactors : conversionFactors).map((factor, index) => (
                  <Table.Tr key={isEditMode ? index : factor.id}>
                    <Table.Td>
                      {isEditMode ? (
                        <Select
                          size="xs"
                          value={factor.drugID.toString()}
                          onChange={(val) => {
                            if (val) {
                              const updated = [...editedConversionFactors];
                              const selectedDrug = drugs.find(d => d.value === val);
                              updated[index].drugID = parseInt(val);
                              updated[index].drugName = selectedDrug?.label || '';
                              setEditedConversionFactors(updated);
                            }
                          }}
                          data={drugs}
                          searchable
                        />
                      ) : (
                        factor.drugName
                      )}
                    </Table.Td>
                    <Table.Td>
                      {isEditMode ? (
                        <Select
                          size="xs"
                          value={factor.routeID.toString()}
                          onChange={(val) => {
                            if (val) {
                              const updated = [...editedConversionFactors];
                              const selectedRoute = routes.find(r => r.value === val);
                              updated[index].routeID = parseInt(val);
                              updated[index].routeName = selectedRoute?.label || '';
                              setEditedConversionFactors(updated);
                            }
                          }}
                          data={routes}
                          searchable
                        />
                      ) : (
                        factor.routeName
                      )}
                    </Table.Td>
                    <Table.Td>
                      {isEditMode ? (
                        <NumberInput
                          size="xs"
                          value={factor.conversionFactor}
                          onChange={(val) => {
                            const updated = [...editedConversionFactors];
                            updated[index].conversionFactor = val as number;
                            setEditedConversionFactors(updated);
                          }}
                          decimalScale={4}
                          step={0.1}
                        />
                      ) : (
                        factor.conversionFactor
                      )}
                    </Table.Td>
                    {isEditMode && (
                      <Table.Td>
                        <ActionIcon
                          size="sm"
                          color="red"
                          variant="subtle"
                          onClick={() => {
                            const updated = editedConversionFactors.filter((_, i) => i !== index);
                            setEditedConversionFactors(updated);
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {(isEditMode ? editedConversionFactors.length : conversionFactors.length) === 0 && (
              <Text ta="center" c="dimmed" py="xl">
                {t('opioidConversionVersionForm.noConversionFactorsFound')}
              </Text>
            )}
            {isEditMode && (
              <Button
                mt="md"
                size="sm"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => {
                  // Use first available options as defaults
                  const defaultDrug = drugs[0];
                  const defaultRoute = routes[0];
                  
                  setEditedConversionFactors([
                    ...editedConversionFactors,
                    {
                      id: 0,
                      drugID: defaultDrug ? parseInt(defaultDrug.value) : 0,
                      drugName: defaultDrug?.label || t('opioidConversionVersionForm.selectDrug'),
                      routeID: defaultRoute ? parseInt(defaultRoute.value) : 0,
                      routeName: defaultRoute?.label || t('opioidConversionVersionForm.selectRoute'),
                      conversionFactor: 1,
                      isActive: true,
                      createdDate: new Date().toISOString(),
                      lastModifiedDate: new Date().toISOString(),
                    } as IOpioidConversionFactor,
                  ]);
                }}
              >
                {t('opioidConversionVersionForm.addConversionFactor')}
              </Button>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        {/* Transdermal Fentanyl Formula */}
        {(fentanylConfig || (isEditMode && editedFentanylConfig)) && (
          <Accordion.Item value="fentanyl">
            <Accordion.Control>
              <Text fw={500}>{t('opioidConversionVersionForm.transdermalFentanylFormula')}</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <div>
                  <Text size="sm" fw={500} mb="xs">{t('opioidConversionVersionForm.conversionFactor')}</Text>
                  {isEditMode ? (
                    <NumberInput
                      size="sm"
                      value={editedFentanylConfig?.fallbackFactor}
                      onChange={(val) => {
                        const updated = { ...editedFentanylConfig };
                        updated.fallbackFactor = val;
                        setEditedFentanylConfig(updated);
                      }}
                      decimalScale={2}
                      step={0.1}
                      style={{ maxWidth: 200 }}
                    />
                  ) : (
                    <Text size="sm" c="dimmed">{fentanylConfig.fallbackFactor}</Text>
                  )}
                </div>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Methadone Formula */}
        {(methadoneConfig || (isEditMode && editedMethadoneConfig)) && (
          <Accordion.Item value="methadone">
            <Accordion.Control>
              <Text fw={500}>{t('opioidConversionVersionForm.methadonePiecewiseFormula')}</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('opioidConversionVersionForm.minDose')}</Table.Th>
                    <Table.Th>{t('opioidConversionVersionForm.maxDose')}</Table.Th>
                    <Table.Th>{t('opioidConversionVersionForm.conversionFactor')}</Table.Th>
                    {isEditMode && <Table.Th style={{ width: 60 }}>{t('opioidConversionVersionForm.actions')}</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(isEditMode ? editedMethadoneConfig?.ranges : methadoneConfig.ranges)?.map((range: any, index: number) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        {isEditMode ? (
                          <NumberInput
                            size="xs"
                            value={range.min}
                            onChange={(val) => {
                              const updated = { ...editedMethadoneConfig };
                              updated.ranges[index].min = val;
                              setEditedMethadoneConfig(updated);
                            }}
                          />
                        ) : (
                          range.min
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isEditMode ? (
                          <NumberInput
                            size="xs"
                            value={range.max || undefined}
                            onChange={(val) => {
                              const updated = { ...editedMethadoneConfig };
                              updated.ranges[index].max = val || null;
                              setEditedMethadoneConfig(updated);
                            }}
                            placeholder="∞"
                          />
                        ) : (
                          range.max || '∞'
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isEditMode ? (
                          <NumberInput
                            size="xs"
                            value={range.factor}
                            onChange={(val) => {
                              const updated = { ...editedMethadoneConfig };
                              updated.ranges[index].factor = val;
                              setEditedMethadoneConfig(updated);
                            }}
                          />
                        ) : (
                          range.factor
                        )}
                      </Table.Td>
                      {isEditMode && (
                        <Table.Td>
                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            onClick={() => {
                              const updated = { ...editedMethadoneConfig };
                              updated.ranges = updated.ranges.filter((_: any, i: number) => i !== index);
                              setEditedMethadoneConfig(updated);
                            }}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {isEditMode && (
                <Button
                  mt="md"
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={12} />}
                  onClick={() => {
                    const updated = { ...editedMethadoneConfig };
                    if (!updated.ranges) updated.ranges = [];
                    updated.ranges.push({ min: 0, max: null, factor: 1 });
                    setEditedMethadoneConfig(updated);
                  }}
                >
                  {t('opioidConversionVersionForm.addRange')}
                </Button>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Buprenorphine Formula */}
        {(buprenorphineConfig || (isEditMode && editedBuprenorphineConfig)) && (
          <Accordion.Item value="buprenorphine">
            <Accordion.Control>
              <Text fw={500}>{t('opioidConversionVersionForm.buprenorphineFormula')}</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <div>
                  <Text size="sm" fw={500} mb="xs">{t('opioidConversionVersionForm.transdermal')}</Text>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">{t('opioidConversionVersionForm.formula')} (read-only)</Text>
                      <Text size="xs" mt={4} c="dimmed" fs="italic">
                        S × (Q / DS) × constantFactor
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">{t('opioidConversionVersionForm.constantFactor')}</Text>
                      {isEditMode ? (
                        <NumberInput
                          size="xs"
                          value={editedBuprenorphineConfig?.transdermal?.constantFactor}
                          onChange={(val) => {
                            const updated = { ...editedBuprenorphineConfig };
                            if (!updated.transdermal) updated.transdermal = {};
                            updated.transdermal.constantFactor = val;
                            setEditedBuprenorphineConfig(updated);
                          }}
                          decimalScale={1}
                          step={0.1}
                          mt={4}
                          style={{ maxWidth: 200 }}
                        />
                      ) : (
                        <Text size="xs" mt={4}>{buprenorphineConfig.transdermal?.constantFactor}</Text>
                      )}
                    </div>
                  </Stack>
                </div>
                <Divider />
                <div>
                  <Text size="sm" fw={500} mb="xs">{t('opioidConversionVersionForm.sublingual')}</Text>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">{t('opioidConversionVersionForm.formula')} (read-only)</Text>
                      <Text size="xs" mt={4} c="dimmed" fs="italic">
                        constantFactor × D
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">{t('opioidConversionVersionForm.constantFactor')}</Text>
                      {isEditMode ? (
                        <NumberInput
                          size="xs"
                          value={editedBuprenorphineConfig?.sublingual?.constantFactor}
                          onChange={(val) => {
                            const updated = { ...editedBuprenorphineConfig };
                            if (!updated.sublingual) updated.sublingual = {};
                            updated.sublingual.constantFactor = val;
                            setEditedBuprenorphineConfig(updated);
                          }}
                          decimalScale={1}
                          step={1}
                          mt={4}
                          style={{ maxWidth: 200 }}
                        />
                      ) : (
                        <Text size="xs" mt={4}>{buprenorphineConfig.sublingual?.constantFactor}</Text>
                      )}
                    </div>
                  </Stack>
                </div>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Risk Thresholds */}
        <Accordion.Item value="risk-thresholds">
          <Accordion.Control>
            <Group>
              <Text fw={500}>MME Risk Thresholds</Text>
              {(isEditMode ? editedRiskThresholds : riskThresholds) && (
                <Badge color="blue">Configured</Badge>
              )}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Configure the MME thresholds that determine risk levels for opioid prescriptions.
              </Text>

              {(isEditMode ? editedRiskThresholds : riskThresholds) ? (
                <Stack gap="lg">
                  {/* Moderate Threshold */}
                  <div>
                    <Text size="sm" fw={500} mb={4}>Moderate Risk Threshold (MME/day)</Text>
                    <Text size="xs" c="dimmed" mb={8}>
                      Patients with daily MME at or above this value will receive a moderate risk alert.
                    </Text>
                    {isEditMode ? (
                      <NumberInput
                        value={editedRiskThresholds?.moderateThreshold}
                        onChange={(val) => setEditedRiskThresholds({ ...editedRiskThresholds, moderateThreshold: val })}
                        min={1}
                        max={1000}
                        step={1}
                        style={{ maxWidth: 200 }}
                        suffix=" MME/day"
                      />
                    ) : (
                      <Text size="sm">{riskThresholds?.moderateThreshold} MME/day</Text>
                    )}
                  </div>

                  {/* High Threshold */}
                  <div>
                    <Text size="sm" fw={500} mb={4}>High Risk Threshold (MME/day)</Text>
                    <Text size="xs" c="dimmed" mb={8}>
                      Patients with daily MME at or above this value will receive a high risk warning.
                    </Text>
                    {isEditMode ? (
                      <NumberInput
                        value={editedRiskThresholds?.highThreshold}
                        onChange={(val) => setEditedRiskThresholds({ ...editedRiskThresholds, highThreshold: val })}
                        min={1}
                        max={1000}
                        step={1}
                        style={{ maxWidth: 200 }}
                        suffix=" MME/day"
                      />
                    ) : (
                      <Text size="sm">{riskThresholds?.highThreshold} MME/day</Text>
                    )}
                  </div>

                  {/* Is Blocking */}
                  <div>
                    <Text size="sm" fw={500} mb={4}>Block High Risk Calculations</Text>
                    <Text size="xs" c="dimmed" mb={8}>
                      If enabled, calculations exceeding the high threshold will be blocked from being saved.
                    </Text>
                    {isEditMode ? (
                      <Select
                        value={editedRiskThresholds?.isBlocking ? 'true' : 'false'}
                        onChange={(val) => setEditedRiskThresholds({ ...editedRiskThresholds, isBlocking: val === 'true' })}
                        data={[
                          { value: 'false', label: 'No - Allow but warn' },
                          { value: 'true', label: 'Yes - Block calculations' }
                        ]}
                        style={{ maxWidth: 250 }}
                      />
                    ) : (
                      <Text size="sm">{riskThresholds?.isBlocking ? 'Yes - Calculations are blocked' : 'No - Calculations are allowed with warning'}</Text>
                    )}
                  </div>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">No risk thresholds configured for this version.</Text>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export default OpioidConversionVersionForm;
